import {
  AgentAgentInteractionDurationColumn,
  CLUSTER_FIELD_PAIRS,
  DateFormatColumns,
  DEFAULT_VISIBLE_COLUMN_LABELS,
  DEFAULT_VISIBLE_COLUMN_ORDER,
  DisableContent,
  getClusteredColumnKey,
  getClusterNameForColumn,
  getInsightColumnDisplayName,
  getRawColumnNameForClusterFilter,
  hasFilterOperandValue,
  isContainsEitherOfOperator,
  ManageColumnCount,
  normalizeContainsEitherOfValues,
  serializeFilterOperandValue,
  SingleArrayValues,
} from './constant';
import {
  ColumnHeaders,
  ColumnPreferenceType,
  ColumnTypes,
  ContactAttributes,
  Conversation,
  FilterCondition,
  FilterOption,
  FilterRequest,
  Insights,
  ClusterData
} from './types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Parses Agent–Agent interaction duration filter input: plain seconds (e.g. "67") or display form "01m:07s".
 * Returns total seconds, or NaN if not parseable.
 */
export function parseAgentAgentInteractionDurationFilterInput(raw: string | number): number {
  if (raw === null || raw === undefined) return NaN;
  const s = String(raw).trim();
  if (!s) return NaN;
  const mMs = /^(\d+)m:(\d+)s?$/i.exec(s);
  if (mMs) {
    return parseInt(mMs[1], 10) * 60 + parseInt(mMs[2], 10);
  }
  if (/^\d+$/.test(s)) {
    return parseInt(s, 10);
  }
  return NaN;
}

function normalizeAgentAgentInteractionDurationForBackend(value: string | string[]): string | string[] {
  if (Array.isArray(value)) {
    return value.map((v) => {
      const sec = parseAgentAgentInteractionDurationFilterInput(v);
      return Number.isNaN(sec) ? String(v) : String(sec);
    });
  }
  const sec = parseAgentAgentInteractionDurationFilterInput(value);
  return Number.isNaN(sec) ? String(value) : String(sec);
}

/**
 * Ensures Agent–Agent interaction duration filter values are sent as seconds (API contract).
 * Use after building `FilterRequest[]` from any source (Insights, User Dashboard charts, Nova, saved filters).
 */
export function normalizeFilterRequestsForBackend(filters: FilterRequest[]): FilterRequest[] {
  return filters.map((f) => {
    const op = f.operationType;
    const isValueDisabledOp =
      op === DisableContent.isEmpty.value || op === DisableContent.isNotEmpty.value;

    let value = f.value;
    if (isContainsEitherOfOperator(op) && !f.isClusterFilter) {
      value = serializeFilterOperandValue(op, value);
    } else if (
      f.columnName === AgentAgentInteractionDurationColumn &&
      !isValueDisabledOp &&
      !f.isClusterFilter
    ) {
      value = normalizeAgentAgentInteractionDurationForBackend(value as string | string[]);
    }

    let clusterFilters = f.clusterFilters ?? [];
    clusterFilters = clusterFilters.map((cf) => {
      const cfOp = cf.operationType ?? '';
      let columnValue = serializeFilterOperandValue(cfOp, cf.columnValue);
      if (
        f.columnName === AgentAgentInteractionDurationColumn &&
        columnValue !== '' &&
        columnValue != null &&
        !isContainsEitherOfOperator(cfOp)
      ) {
        columnValue = normalizeAgentAgentInteractionDurationForBackend(
          columnValue as string
        ) as string;
      }
      return { ...cf, columnValue };
    });

    return { ...f, value, clusterFilters };
  });
}

export const convertToFilterOptionArray = (data: string[]): any[] => {
  return data
    ?.filter((item) => item && item.trim() !== '') // Remove empty items
    ?.map((item, index) => ({
      filterId: (index + 1).toString(),
      name: item,
      isSelected: true,
    }));
};

export const formatOptions = (names: ColumnHeaders[]): FilterOption[] => {
  return names.map((item, index) => {
    let visibilityCount = 0;

    if (item.visibility) visibilityCount++;

    return {
      filterId: item.key,
      name: item.name,
      number: item.number,
      type: item.type,
      isSelected: visibilityCount <= ManageColumnCount ? item.visibility : false,
      tempIsSelected: visibilityCount <= ManageColumnCount ? item.visibility : false,
    };
  });
};

export const formatToUpdateUserColumnPreference = (columns: FilterOption[]): ColumnPreferenceType[] => {
  return columns.map((item, index) => {
    return {
      column_data_type: item.type ?? '',
      column_label: item.name,
      column_name: item.filterId,
      column_number: item.number ?? 0,
      column_visibility: item.tempIsSelected ?? false,
    };
  });
};

export const transformTableHeaders = (TableHeaders: ColumnHeaders[]) => {
  return TableHeaders.map((header) => ({
    label: getInsightColumnDisplayName(header.key, header.name),
    value: header.key,
    type: header.type as ColumnTypes,
  }));
};

/** Keep incoming column order; append any missing clustered columns at the end (hidden). */
export const ensureInsightClusterColumnPreferences = (
  preferences: Array<ColumnPreferenceType>
): Array<ColumnPreferenceType> => {
  const used = new Set<string>();
  const result: Array<ColumnPreferenceType> = [];

  for (const pref of preferences) {
    if (used.has(pref.column_name)) continue;
    const pair = findPairForPreference(pref.column_name);
    if (pair?.rawKey === pref.column_name) {
      result.push({ ...pref, column_label: pair.rawLabel });
    } else if (pair?.clusteredKey === pref.column_name) {
      result.push({ ...pref, column_label: pair.clusteredLabel });
    } else {
      result.push(pref);
    }
    used.add(pref.column_name);
  }

  let nextNumber = Math.max(0, ...result.map((pref) => pref.column_number ?? 0)) + 1;
  for (const pair of CLUSTER_FIELD_PAIRS) {
    if (used.has(pair.clusteredKey)) continue;
    result.push({
      column_data_type: pair.clusteredType,
      column_label: pair.clusteredLabel,
      column_name: pair.clusteredKey,
      column_number: nextNumber++,
      column_visibility: false,
    });
    used.add(pair.clusteredKey);
  }

  return result;
};

/** Place the product default columns first, in the specified order. */
export const applyDefaultVisibleColumnOrder = (
  preferences: Array<ColumnPreferenceType>
): Array<ColumnPreferenceType> => {
  const byKey = new Map(preferences.map((pref) => [pref.column_name, pref]));
  const used = new Set<string>();
  const result: Array<ColumnPreferenceType> = [];

  DEFAULT_VISIBLE_COLUMN_ORDER.forEach((columnName, index) => {
    const pref = byKey.get(columnName);
    if (!pref) return;
    result.push({
      ...pref,
      column_label: DEFAULT_VISIBLE_COLUMN_LABELS[columnName] ?? pref.column_label,
      column_number: index + 1,
    });
    used.add(columnName);
  });

  let nextNumber = result.length + 1;
  for (const pref of preferences) {
    if (used.has(pref.column_name)) continue;
    result.push({ ...pref, column_number: nextNumber++ });
    used.add(pref.column_name);
  }

  return result;
};

function findPairForPreference(columnName: string) {
  return CLUSTER_FIELD_PAIRS.find((pair) => pair.rawKey === columnName || pair.clusteredKey === columnName);
}

/** Point existing cluster filters at the clustered column so the UI matches table/filter labels. */
export const migrateClusterFilterConditions = (conditions: FilterCondition[]): FilterCondition[] => {
  return conditions.map((condition) => {
    const columnKey = condition.selectedColumn?.value;
    if (!condition.isCluster || !columnKey) {
      if (columnKey && condition.selectedColumn) {
        return {
          ...condition,
          selectedColumn: {
            ...condition.selectedColumn,
            label: getInsightColumnDisplayName(columnKey, condition.selectedColumn.label),
          },
        };
      }
      return condition;
    }

    const clusteredKey = getClusteredColumnKey(columnKey);
    return {
      ...condition,
      clusterName: getClusterNameForColumn(columnKey) ?? condition.clusterName,
      selectedColumn: {
        ...condition.selectedColumn,
        value: clusteredKey,
        label: getInsightColumnDisplayName(clusteredKey, condition.selectedColumn?.label),
      },
    };
  });
};

/** Accepts either an array of preferences or API response shape { preferences: [...] }. */
export const transformColumnPreferences = (
  preferences: Array<ColumnPreferenceType> | { preferences?: Array<ColumnPreferenceType> }
): Array<ColumnHeaders> => {
  const list = Array.isArray(preferences) ? preferences : (preferences?.preferences ?? []);
  const withClusterColumns = applyDefaultVisibleColumnOrder(ensureInsightClusterColumnPreferences(list));
  return withClusterColumns.map((preference) => {
    const header = {
      name: getInsightColumnDisplayName(preference.column_name, preference.column_label),
      key: preference.column_name,
      type: preference.column_data_type,
      number: preference.column_number,
      visibility: preference.column_visibility,
    };

    if (SingleArrayValues.includes(preference.column_name)) {
      header.type = ColumnTypes.singleArray;
    }

    return header;
  });
};

export const transformTestToFilters = (rawFilterData: FilterCondition[]): { data: FilterRequest[]; error: boolean } => {
  let error = false;

  if (rawFilterData.length) {
    // First, filter out conditions that don't have valid values (except for isEmpty/isNotEmpty operations)
    const validConditions = rawFilterData.filter((item) => {
      // Skip conditions without selected column
      if (!item.selectedColumn?.value) {
        return false;
      }

      // For cluster filters, validate only via cluster data (no top-level selectedOperation required)
      if (item.isCluster && item.clusterData && item.clusterData.length > 0) {
        const validClusterData = item.clusterData.filter((cluster) => {
          const clusterOp = cluster.selectedOperation?.value;
          if (
            clusterOp === DisableContent.isEmpty.value ||
            clusterOp === DisableContent.isNotEmpty.value
          ) {
            return true;
          }
          return !!cluster.selectedCluster?.value && hasFilterOperandValue(clusterOp, cluster.value);
        });
        return validClusterData.length > 0;
      }

      // For non-cluster filters, require top-level operation and value
      if (!item.selectedOperation?.value) {
        return false;
      }
      // For isEmpty/isNotEmpty operations, value can be empty
      if (
        item.selectedOperation?.value === DisableContent.isEmpty.value ||
        item.selectedOperation?.value === DisableContent.isNotEmpty.value
      ) {
        return true;
      }
      // For non-cluster filters, check if value is not empty (support string or array e.g. queue names)
      if (isContainsEitherOfOperator(item.selectedOperation?.value)) {
        return normalizeContainsEitherOfValues(item.value).length > 0;
      }
      if (Array.isArray(item.value)) {
        return item.value.length > 0;
      }
      return item.value != null && item.value !== '' && String(item.value).trim().length > 0;
    });

    // Transform only valid conditions
    const transformedData = validConditions.map((item) => {
      const isClusterCondition = item.isCluster && item.clusterData && item.clusterData.length > 0;
      const selectedColumnKey = item.selectedColumn?.value ?? '';
      const columnName = isClusterCondition
        ? getRawColumnNameForClusterFilter(selectedColumnKey)
        : selectedColumnKey;
      const clusterName = isClusterCondition
        ? (getClusterNameForColumn(selectedColumnKey) ?? item.clusterName ?? '')
        : (item.clusterName ?? '');
      // For cluster conditions, top-level operationType may be unset; use first cluster's operation or a safe default so backend doesn't error
      const defaultOperationType = isClusterCondition && item.clusterData?.[0]?.selectedOperation?.value
        ? item.clusterData[0].selectedOperation.value
        : 'contains';
      const operationType = item.selectedOperation?.value ?? defaultOperationType;
      const requestValue: string | string[] = serializeFilterOperandValue(operationType, item.value);
      const filter: FilterRequest = {
        logicalOperator: item.operator || 'AND', // Default to "AND" if operator is null
        columnName,
        operationType,
        value: requestValue,
        isClusterFilter: item.isCluster || false,
        clusterFilters: [],
      };

      // Only include clusterFilters when this is actually a cluster condition
      if (isClusterCondition && item.clusterData) {
        filter.clusterFilters = item.clusterData
          .filter((rootCause: ClusterData) => {
            const op = rootCause.selectedOperation?.value;
            if (
              op === DisableContent.isEmpty.value ||
              op === DisableContent.isNotEmpty.value
            ) {
              return true;
            }
            return !!rootCause.selectedCluster?.value && hasFilterOperandValue(op, rootCause.value);
          })
          .map((rootCause: ClusterData) => {
            const op = rootCause.selectedOperation?.value ?? '';
            return {
              clusterName,
              clusterValue: rootCause.selectedCluster?.value ?? '',
              columnName,
              operationType: op,
              columnValue: serializeFilterOperandValue(op, rootCause.value),
            };
          });
        // Backend may reject empty top-level value for cluster filters; use first cluster's value so we never send ""
        const raw = filter.value;
        const isEmpty = raw === '' || (typeof raw === 'string' && !raw.trim()) || (Array.isArray(raw) && raw.length === 0);
        if (isEmpty && filter.clusterFilters.length > 0) {
          const first = filter.clusterFilters[0];
          const firstSerialized = serializeFilterOperandValue(first.operationType, first.columnValue);
          if (Array.isArray(firstSerialized)) {
            filter.value = firstSerialized.length ? firstSerialized : (first.clusterValue || ' ');
          } else if (firstSerialized.trim()) {
            filter.value = firstSerialized;
          } else {
            filter.value = first.clusterValue && first.clusterValue.trim() ? first.clusterValue : ' ';
          }
        }
      }

      return filter;
    });

    // Ensure no filter with empty columnName is sent (backend may reject or throw)
    const safeData = transformedData.filter((f) => f.columnName && f.operationType);

    return { data: normalizeFilterRequestsForBackend(safeData), error };
  }

  return { data: [], error };
};

export const convertToFilterCondition = (filters: FilterRequest[]): FilterCondition[] => {
  const conditions = filters.map((filter, index) => {
    const uiColumnName = filter.isClusterFilter
      ? getClusteredColumnKey(filter.columnName)
      : filter.columnName;
    const displayName = getInsightColumnDisplayName(uiColumnName, filter.columnName);

    const op = filter.operationType;
    const isValueDisabledOp = op === DisableContent.isEmpty.value || op === DisableContent.isNotEmpty.value;
    const clusterFilters = filter.clusterFilters ?? [];
    const restoredValue = isContainsEitherOfOperator(op)
      ? normalizeContainsEitherOfValues(filter.value)
      : isValueDisabledOp
        ? ''
        : filter.value;
    // Map each filter to FilterCondition (normal conditions: clear value when operator is is_empty/is_not_empty)
    const filterCondition: FilterCondition = {
      index: index + 1,
      operator: filter.logicalOperator,
      selectedColumn: {
        value: uiColumnName,
        label: displayName,
      },
      selectedOperation: {
        value: filter.operationType,
        label: isContainsEitherOfOperator(op) ? 'contains either of' : filter.operationType,
      },
      value: restoredValue,
      isCluster: filter.isClusterFilter,
      clusterName: clusterFilters[0]?.clusterName || getClusterNameForColumn(filter.columnName),
      clusterData: (clusterFilters ?? []).map((clusterFilter, clusterIndex) => {
        const clusterOp = clusterFilter.operationType ?? '';
        return {
          index: clusterIndex + 1,
          selectedCluster: {
            value: clusterFilter.clusterValue,
            label: clusterFilter.clusterValue,
          },
          selectedOperation: {
            value: clusterFilter.operationType,
            label: isContainsEitherOfOperator(clusterOp) ? 'contains either of' : clusterFilter.operationType,
          },
          value: serializeFilterOperandValue(clusterOp, clusterFilter.columnValue),
        };
      }),
    };

    return filterCondition;
  });

  return migrateClusterFilterConditions(conditions);
};

/** Normalize concept API DataFilter into the existing FilterRequest / ConceptDataFilter shape. */
export const normalizeConceptDataFilter = (filter: {
  logicalOperator?: string | null;
  columnName?: string | null;
  operationType?: string;
  value?: any;
  isClusterFilter?: boolean;
  clusterFilters?: Array<{
    clusterName?: string;
    clusterValue?: string;
    columnName?: string;
    operationType?: string;
    columnValue?: string | string[];
  }>;
}): FilterRequest => {
  const logicalOperator =
    filter.logicalOperator === 'AND' || filter.logicalOperator === 'OR' ? filter.logicalOperator : 'OR';
  const operationType = filter.operationType || 'contains';

  return {
    logicalOperator,
    columnName: filter.columnName || filter.clusterFilters?.[0]?.clusterName || '',
    operationType,
    value: serializeFilterOperandValue(operationType, filter.value),
    isClusterFilter: !!filter.isClusterFilter,
    clusterFilters: (filter.clusterFilters || []).map((cf) => {
      const clusterOp = cf.operationType ?? filter.operationType ?? 'contains';
      return {
        clusterName: cf.clusterName ?? '',
        clusterValue: cf.clusterValue ?? '',
        columnName: cf.columnName ?? filter.columnName ?? '',
        operationType: clusterOp,
        columnValue: serializeFilterOperandValue(clusterOp, cf.columnValue),
      };
    }),
  };
};

// Function to convert Conversation array to TableHeaderType array
export const convertConversationsToTableHeaderType = (
  conversations: Array<Conversation>
): Array<{ [key: string]: string }> => {
  const result: Array<any> = [];

  conversations.forEach((conversation) => {
    let oneRecord = {};
    // For each property in the Conversation object
    for (const key in conversation) {
      if (conversation.hasOwnProperty(key)) {
        if (key == 'Insights') {
          for (const insightsKey in conversation[key as keyof Conversation] as Insights) {
            oneRecord = {
              ...oneRecord,
              [insightsKey]:
                conversation.Insights[insightsKey as keyof Insights] !== null
                  ? conversation.Insights[insightsKey as keyof Insights]
                  : 'null',
            };
          }
        } else if (key == 'ContactAttributes') {
          for (const contactAttributeKey in conversation[key as keyof Conversation] as ContactAttributes) {
            oneRecord = {
              ...oneRecord,
              [contactAttributeKey]:
                conversation.ContactAttributes[contactAttributeKey as keyof ContactAttributes] !== null
                  ? conversation.ContactAttributes[contactAttributeKey as keyof ContactAttributes]
                  : 'null',
            };
          }
        } else {
          // Push the key-value pair as a TableHeaderType object
          oneRecord = {
            ...oneRecord,
            [key]: conversation[key as keyof Conversation] !== null ? conversation[key as keyof Conversation] : 'null',
          };
        }
      }
    }

    result.push(oneRecord);
  });

  return result;
};

export const delayLoading = (seconds: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, seconds * 1000));
};

const isValidDate = (dateString: string) => {
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

const formatDate = (dateString: string) => {
  const date = dayjs(dateString);

  return date.format('DD MMM YYYY hh:mm:ss A');
};

const formatValue = (key: any, value: any): string => {
  if (DateFormatColumns.includes(key)) {
    return value !== '' && value !== 'NA' && value !== null ? formatDate(value) : 'null';
  }
  return value !== null ? value : 'null';
};

/** Top-level conversation keys; column order for raw table */
const CONVERSATION_TOP_KEYS = ['ContactId', 'ContactTimestamp'] as const;

/** Preferred Insights column order for raw table (matches default Data View order) */
const INSIGHTS_KEY_ORDER = [
  'PrimaryTopic', 'Intent', 'Topics', 'RootCause', 'CustomerPainPoint',
  'Trigger', 'Outcome', 'Summary', 'CallResolution',
  'CustomerSentimentAtStart', 'CustomerSentimentAtEnd',
  'TopicsClustered', 'RootCauseClustered', 'CustomerPainPointClustered', 'OutcomeClustered', 'TriggerClustered',
] as const;

export const convertConversationsToRowsColumns = (
  conversations: Array<Conversation>
): { rows: Array<{ [key: string]: string }>; columns: Array<{ prop?: string; name?: string }> } => {
  const rows: Array<any> = [];
  const columnsSet: Set<string> = new Set();

  conversations.forEach((conversation) => {
    let oneRecord: Record<string, unknown> = {};

    for (const key in conversation) {
      if (conversation.hasOwnProperty(key)) {
        if (key === 'Insights') {
          const insights = conversation.Insights as unknown as Record<string, unknown>;
          for (const insightsKey in insights) {
            if (Object.prototype.hasOwnProperty.call(insights, insightsKey)) {
              oneRecord[insightsKey] = formatValue(insightsKey, conversation.Insights[insightsKey as keyof Insights]);
              columnsSet.add(insightsKey);
            }
          }
        } else if (key === 'ContactAttributes') {
          const attrs = conversation.ContactAttributes as unknown as Record<string, unknown>;
          for (const contactAttributeKey in attrs) {
            if (Object.prototype.hasOwnProperty.call(attrs, contactAttributeKey)) {
              oneRecord[contactAttributeKey] = formatValue(
                contactAttributeKey,
                conversation.ContactAttributes[contactAttributeKey as keyof ContactAttributes]
              );
              columnsSet.add(contactAttributeKey);
            }
          }
        } else {
          oneRecord[key] = formatValue(key, conversation[key as keyof Conversation]);
          columnsSet.add(key);
        }
      }
    }

    rows.push(oneRecord);
  });

  // Build columns in deterministic order: top-level → Insights (preferred order) → ContactAttributes (insertion order)
  const orderedColumns: string[] = [];
  const seen = new Set<string>();

  for (const k of CONVERSATION_TOP_KEYS) {
    if (columnsSet.has(k)) {
      orderedColumns.push(k);
      seen.add(k);
    }
  }
  for (const k of INSIGHTS_KEY_ORDER) {
    if (columnsSet.has(k) && !seen.has(k)) {
      orderedColumns.push(k);
      seen.add(k);
    }
  }
  for (const col of columnsSet) {
    if (!seen.has(col)) {
      orderedColumns.push(col);
      seen.add(col);
    }
  }

  const columns = orderedColumns.map((prop) => ({ prop }));

  return { rows, columns };
};
